import React, { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { MarketplaceCookieChecker } from '@shipstation/connections-ui';
import { NotificationService, NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';

import { getPartitionNumber } from '../services/partition-service';
import { ResumeSsrActivationPath } from '../constants/cookies';
import { CookieService } from '../services/cookie-service';
import { refreshStoreList } from '../store/seller/seller.actions';
import { SellerProperties } from '../store/seller/seller-properties';
import messages from '../pages/onboard/steps/marketplace-step/messages';
import { useEnableHomePage } from '../feature-flags/hooks/use-enable-home-page';
import { useEnableHomePage2 } from '../feature-flags/hooks/use-enable-home-page2';
import { useIsTrialActive } from './use-is-trial-active';
import { FTU_WIZARD_PATH } from '../routes/constants';

const SHOPIFY_MARKETPLACE_ID = '14';

// Determine redirect path based on feature flags and trial status
export const getRedirectPath = (
  enableHomePage,
  enableHomePage2,
  isTrialActive,
  isSetupComplete,
  onboardingPath = '/onboard',
  storeSettingsPath = '/settings/stores',
  resumeSsrActivationPath,
) => {
  if (resumeSsrActivationPath) {
    return resumeSsrActivationPath;
  }

  // Old onboarding flow: both home page flags are false and setup incomplete
  if (!enableHomePage && !enableHomePage2 && !isSetupComplete) {
    return onboardingPath;
  }

  // FTU Wizard: home page 2 enabled and trial active
  if (enableHomePage2 && isTrialActive) {
    return FTU_WIZARD_PATH;
  }

  // Store settings: all other cases
  return storeSettingsPath;
};

export const useMarketplaceCookierChecker = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const storeSettingsPath = '/settings/stores';
  const onboardingPath = '/onboard';
  const isSetupComplete = useSelector((state) => new SellerProperties({ state }).isSetupComplete);
  const enableHomePage = useEnableHomePage();
  const enableHomePage2 = useEnableHomePage2();
  const isTrialActive = useIsTrialActive();
  const resumeSsrActivationPath = CookieService.getCookieValue(ResumeSsrActivationPath);

  const redirectToPath = getRedirectPath(
    enableHomePage,
    enableHomePage2,
    isTrialActive,
    isSetupComplete,
    onboardingPath,
    storeSettingsPath,
    resumeSsrActivationPath,
  );

  useEffect(() => {
    MarketplaceCookieChecker(
      getPartitionNumber(),
      CookieService.getCookieValue('auth'),
      (data) => {
        history.push(redirectToPath);
        dispatch(refreshStoreList());
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_SUCCESS,
          title: <FormattedMessage defaultMessage='Success' description='Success' id='yg2HFT' />,
          content: (
            <FormattedMessage
              defaultMessage='Successfully added {marketplaceName}.'
              description='Marketplace connection success'
              id='co/iPJ'
              values={{
                marketplaceName: data.name,
              }}
            />
          ),
          duration: 3000,
        });
        if (data.marketplaceId === SHOPIFY_MARKETPLACE_ID) {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_SUCCESS,
            title: <FormattedMessage {...messages.saveSuccessTitle} />,
            content: (
              <FormattedMessage
                {...messages.saveSuccessMessage}
                values={{
                  a: (chunks) => <Link to='/orders/awaiting-shipment'>{chunks}</Link>,
                }}
              />
            ),
            dontHide: true,
          });
        }
      },
      (data) => {
        history.push(redirectToPath);
        NotificationService.showNotification({
          notificationType: NOTIFICATION_TYPE_ERROR,
          title: <FormattedMessage defaultMessage='Error' description='Error' id='6/PjjV' />,
          content: (
            <FormattedMessage
              defaultMessage='Failed to add {marketplaceName}. Please try again.'
              description='Marketplace connection success'
              id='abzY5c'
              values={{
                marketplaceName: data.name,
              }}
            />
          ),
          duration: 3000,
        });
      },
    );
  }, [history, dispatch, redirectToPath]);
};
